/**
 * SpecialistLabUpload.jsx — iter658
 * Inline modal launched from MyDataHub. Drag-and-drop / file picker for DUTCH
 * or DNA PDF. Calls /api/bhi/specialist-lab/upload, shows parsed result.
 */
import React, { useState } from "react";
import axios from "axios";
import { Upload, X, Loader2, CheckCircle2, AlertCircle, FileText, Dna, FlaskConical } from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";

const TX = {
  bg: {
    title: "Качи DUTCH или ДНК тест",
    drag: "Пуснете PDF тук или кликнете да изберете",
    typeHint: "Тип на теста (по избор):",
    auto: "Автоматично",
    dutch: "DUTCH (хормони)",
    genomic: "ДНК / Genomic",
    uploading: "Анализираме PDF…",
    success: "✓ Успешно анализиран",
    error: "Грешка при анализа",
    confidence: "Сигурност",
    flagsFound: "намерени маркери",
    parseAnother: "Качи още един",
    close: "Затвори",
    privacy: "Файловете се обработват чрез сигурен AI и се пазят само Вашите структурирани резултати. Можете да изтриете по всяко време.",
  },
  en: { title: "Upload DUTCH or DNA test", drag: "Drop PDF here or click to choose",
         typeHint: "Test type (optional):", auto: "Auto-detect", dutch: "DUTCH (hormones)",
         genomic: "DNA / Genomic", uploading: "Analysing PDF…", success: "✓ Parsed successfully",
         error: "Parse failed", confidence: "Confidence", flagsFound: "flags found",
         parseAnother: "Upload another", close: "Close",
         privacy: "Files are processed via secure AI and only your structured results are kept." },
  es: { title: "Subir DUTCH o ADN", drag: "Suelte el PDF aquí o haga clic", typeHint: "Tipo (opcional):",
         auto: "Auto", dutch: "DUTCH (hormonas)", genomic: "ADN / Genómico", uploading: "Analizando PDF…",
         success: "✓ Analizado", error: "Error de análisis", confidence: "Confianza",
         flagsFound: "marcadores", parseAnother: "Subir otro", close: "Cerrar",
         privacy: "Procesado por IA segura; sólo conservamos los resultados estructurados." },
};

export default function SpecialistLabUpload({ open, onClose, lang = "bg", onParsed }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const t = TX[lang] || TX.bg;
  const [hint, setHint] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [drag, setDrag] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setLoading(true); setErr(null); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (hint) fd.append("hint", hint);
      const { data } = await axios.post(`${API}/bhi/specialist-lab/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResult(data);
      onParsed?.(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="speclab-modal"
    >
      <div
        className={`max-w-2xl w-full max-h-[85vh] overflow-y-auto rounded-2xl border p-6 shadow-2xl ${
          isDark ? "bg-[hsl(222,47%,11%)] border-white/10 text-white" : "bg-white border-gray-200"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-extrabold flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-purple-500" /> {t.title}
          </h3>
          <button onClick={onClose} className="opacity-60 hover:opacity-100" data-testid="speclab-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!result && !loading && (
          <>
            <div className="mb-3 flex items-center gap-2 text-xs">
              <span className="opacity-70">{t.typeHint}</span>
              {[["", t.auto], ["dutch", t.dutch], ["genomic", t.genomic]].map(([v, l]) => (
                <button
                  key={v}
                  onClick={() => setHint(v)}
                  className={`px-2 py-1 rounded-full font-semibold ${
                    hint === v
                      ? "bg-purple-500 text-white"
                      : (isDark ? "bg-white/5 hover:bg-white/10" : "bg-gray-100 hover:bg-gray-200")
                  }`}
                  data-testid={`speclab-hint-${v || "auto"}`}
                >{l}</button>
              ))}
            </div>

            <label
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={onDrop}
              className={`block cursor-pointer p-10 rounded-xl border-2 border-dashed text-center transition-colors ${
                drag
                  ? "border-purple-500 bg-purple-500/10"
                  : (isDark ? "border-white/20 hover:border-white/40" : "border-gray-300 hover:border-gray-500")
              }`}
              data-testid="speclab-drop"
            >
              <Upload className="w-10 h-10 mx-auto opacity-50 mb-2" />
              <p className="text-sm font-semibold">{t.drag}</p>
              <input
                type="file" accept="application/pdf" className="hidden"
                onChange={(e) => upload(e.target.files?.[0])}
                data-testid="speclab-file-input"
              />
            </label>
            <p className="text-[10px] opacity-60 mt-2 italic">{t.privacy}</p>
          </>
        )}

        {loading && (
          <div className="text-center py-10">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-500" />
            <p className="mt-3 text-sm">{t.uploading}</p>
            <p className="text-[11px] opacity-60 mt-1">Може да отнеме до 60 секунди…</p>
          </div>
        )}

        {err && (
          <div className="p-4 rounded-xl bg-red-500/10 text-red-600 dark:text-red-400 flex items-start gap-2" data-testid="speclab-error">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <div className="text-sm space-y-2">
              <div className="font-semibold">{t.error}</div>
              <div className="text-xs leading-relaxed opacity-90">{err}</div>
              <button
                onClick={() => { setErr(null); setResult(null); }}
                className="mt-2 px-3 py-1.5 rounded-lg text-xs font-bold bg-red-500/20 hover:bg-red-500/30"
                data-testid="speclab-retry-btn"
              >
                Опитайте отново с друг файл
              </button>
            </div>
          </div>
        )}

        {result && !loading && (
          <div className="space-y-3" data-testid="speclab-result">
            <div className="p-4 rounded-xl bg-emerald-500/10 flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold">{t.success}</div>
                <div className="text-xs opacity-80 mt-0.5">
                  {result.detected_type === "dutch" ? <FlaskConical className="inline w-3 h-3 mr-1" /> : <Dna className="inline w-3 h-3 mr-1" />}
                  {result.detected_type?.toUpperCase()} · {t.confidence}: {result.extraction_confidence}
                </div>
              </div>
            </div>

            {(Object.keys(result.flags || {}).length > 0 || (result.markers_extracted || 0) > 0) && (
              <div className="p-3 rounded-xl border border-amber-500/30 bg-amber-500/5">
                <div className="text-xs font-bold opacity-80 mb-2">
                  {(result.markers_extracted || 0) > 0
                    ? `${result.markers_extracted} ${t.flagsFound}`
                    : `${Object.keys(result.flags || {}).filter(k => !k.endsWith("recommendation")).length} ${t.flagsFound}`}
                </div>
                <ul className="space-y-1.5 text-xs">
                  {Object.entries(result.flags || {})
                    .filter(([k]) => k.endsWith("recommendation"))
                    .map(([k, v]) => (
                      <li key={k} className="flex items-start gap-1.5">
                        <span className="text-amber-500 shrink-0">▸</span>
                        <span>{v}</span>
                      </li>
                    ))}
                </ul>
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => { setResult(null); setErr(null); }}
                className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-500/20 hover:opacity-90"
                data-testid="speclab-parse-another"
              >
                {t.parseAnother}
              </button>
              <button
                onClick={onClose}
                className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:opacity-90"
                data-testid="speclab-done"
              >
                {t.close}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
